import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ticketsService } from '../api/ticketsService';
import './TicketDetail.css';

export default function TicketDetail() {
  const { id } = useParams();
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [statusUpdate, setStatusUpdate] = useState({ status: '', assigned_to_id: '' });
  const role = localStorage.getItem('userRole');

  const loadTicket = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await ticketsService.get(id);
      setTicket(res.data);
      setMessages(res.data.messages || []);
    } catch (err) {
      setError('Failed to load ticket');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadAudit = async () => {
    try {
      const res = await ticketsService.getAudit(id);
      setAudit(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    loadTicket();
    loadAudit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!messageBody.trim()) return;
    try {
      const res = await ticketsService.addMessage(id, { body: messageBody });
      setMessages([...messages, res.data]);
      setMessageBody('');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to send message');
      console.error(err);
    }
  };

  const updateStatus = async (e) => {
    e.preventDefault();
    try {
      const payload = {};
      if (statusUpdate.status) payload.status = statusUpdate.status;
      if (statusUpdate.assigned_to_id) payload.assigned_to_id = Number(statusUpdate.assigned_to_id);
      const res = await ticketsService.updateStatus(id, payload);
      setTicket(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to update ticket');
      console.error(err);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!ticket) return <div className="error">Ticket not found</div>;

  return (
    <div className="ticket-detail">
      <div className="ticket-header">
        <div>
          <h2>Ticket #{ticket.id}</h2>
          <p className="subtext">{ticket.title}</p>
        </div>
        <div className="pills">
          <span className={`pill ${ticket.status}`}>{ticket.status}</span>
          <span className={`pill priority-${ticket.priority}`}>{ticket.priority}</span>
        </div>
      </div>

      <div className="card">
        <h4>Description</h4>
        <p>{ticket.description}</p>
        <div className="meta">
          <span>Company ID: {ticket.company_id}</span>
          <span>Cert Body ID: {ticket.cert_body_id}</span>
          <span>Created By: {ticket.created_by_id}</span>
          {ticket.assigned_to_id && <span>Assigned To: {ticket.assigned_to_id}</span>}
        </div>
      </div>

      {(role === 'third_party_admin' || role === 'super_admin') && (
        <div className="card">
          <h4>Status & Assignment</h4>
          <form className="status-form" onSubmit={updateStatus}>
            <select value={statusUpdate.status} onChange={(e) => setStatusUpdate({ ...statusUpdate, status: e.target.value })}>
              <option value="">Select status</option>
              <option value="open">Open</option>
              <option value="pending">Pending</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
            <input
              type="number"
              placeholder="Assignee user id (cert body)"
              value={statusUpdate.assigned_to_id}
              onChange={(e) => setStatusUpdate({ ...statusUpdate, assigned_to_id: e.target.value })}
            />
            <button type="submit" className="primary">Update</button>
          </form>
        </div>
      )}

      <div className="card">
        <h4>Messages</h4>
        <div className="messages">
          {messages.map((m) => (
            <div key={m.id} className="message">
              <div className="message-meta">
                <span>Author: {m.author_id}</span>
                <span>{new Date(m.created_at).toLocaleString()}</span>
              </div>
              <div>{m.body}</div>
            </div>
          ))}
          {messages.length === 0 && <div className="empty">No messages yet.</div>}
        </div>
        <form className="message-form" onSubmit={sendMessage}>
          <textarea rows={3} value={messageBody} onChange={(e) => setMessageBody(e.target.value)} placeholder="Type a message" />
          <div className="actions" style={{ justifyContent: 'flex-end' }}>
            <button type="submit" className="primary">Send</button>
          </div>
        </form>
      </div>

      <div className="card">
        <h4>Audit Trail</h4>
        <div className="audit-list">
          {audit.map((a) => (
            <div key={a.id} className="audit-row">
              <div>{a.action}</div>
              <div>Actor: {a.actor_id}</div>
              <div>{new Date(a.created_at).toLocaleString()}</div>
            </div>
          ))}
          {audit.length === 0 && <div className="empty">No audit entries.</div>}
        </div>
      </div>
    </div>
  );
}
